import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, CircleCheck, Eye, Lock, RotateCcw } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import { RenderedHtml } from 'modules/editor';
import {
  useMarkLearned, useRetryMistake, useRevealMistake,
  type Mistake, type MistakeAnswer, type RetryAnswer, type RetryForm,
} from 'modules/mistakes';

const emptyAnswer = (form: RetryForm): RetryAnswer =>
  form.kind === 'multi' ? [] : form.kind === 'blanks' ? {} : '';

const isAnswered = (form: RetryForm, answer: RetryAnswer): boolean => {
  if (form.kind === 'multi') return Array.isArray(answer) && answer.length > 0;
  if (form.kind === 'blanks') {
    const values = answer as Record<string, string>;
    return form.blanks.every((id) => Boolean(values[id]?.trim()));
  }
  return typeof answer === 'string' && answer.trim().length > 0;
};

/** The same inputs a test uses, kept small: option buttons, a text box, or one box per blank. */
function RetryInput({ form, value, onChange, disabled }: {
  form: RetryForm; value: RetryAnswer; onChange: (next: RetryAnswer) => void; disabled: boolean;
}) {
  const { t } = useTranslation();
  if (form.kind === 'choice' || form.kind === 'multi') {
    const selected = new Set(Array.isArray(value) ? value : [value]);
    const toggle = (id: string) => {
      if (form.kind === 'choice') return onChange(id);
      const list = Array.isArray(value) ? value : [];
      onChange(list.includes(id) ? list.filter((v) => v !== id) : [...list, id]);
    };
    return (
      <div role="group" aria-label={t('mistakes.your_new_answer')} className="grid gap-2">
        {form.options.map((option) => {
          const on = selected.has(option.id);
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => toggle(option.id)}
              className={cn(
                'flex min-h-[44px] items-center gap-3 rounded-md border px-3 py-2 text-left text-[14px] transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60',
                on ? 'border-primary bg-primary/5 text-foreground' : 'border-border bg-card hover:bg-muted/60',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center border',
                  form.kind === 'multi' ? 'rounded-[4px]' : 'rounded-full',
                  on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50',
                )}
              >
                {on && <Check className="h-3 w-3" />}
              </span>
              <RenderedHtml html={option.text} inline />
            </button>
          );
        })}
      </div>
    );
  }
  if (form.kind === 'blanks') {
    const values = (value as Record<string, string>) ?? {};
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {form.blanks.map((id, index) => (
          <Input
            key={id}
            aria-label={t('mistakes.blank_label', { n: index + 1 })}
            placeholder={t('mistakes.blank_label', { n: index + 1 })}
            value={values[id] ?? ''}
            disabled={disabled}
            maxLength={300}
            onChange={(e) => onChange({ ...values, [id]: e.target.value })}
          />
        ))}
      </div>
    );
  }
  return (
    <Input
      aria-label={t('mistakes.your_new_answer')}
      placeholder={t('mistakes.type_answer')}
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      maxLength={300}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** The right answer and, when the attempt's analysis is unlocked, the author's explanation. */
function AnswerReveal({ answer, attemptId }: { answer: MistakeAnswer; attemptId: string }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="rounded-md border border-success-200 bg-success-50 px-3 py-2 text-[13.5px] dark:border-success-900/50 dark:bg-success-900/20">
        <p className="text-[12px] font-medium text-success-700 dark:text-success-400">{t('mistakes.correct_answer')}</p>
        <p className="mt-0.5 font-medium text-foreground">{answer.correctAnswer ? <RenderedHtml html={answer.correctAnswer} inline /> : '—'}</p>
      </div>
      {answer.explanation && (
        <div className="rounded-md bg-muted/60 px-3 py-2.5 text-[13.5px] leading-[1.55] text-foreground/90">
          <RenderedHtml html={answer.explanation} />
        </div>
      )}
      {answer.explanationLocked && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          {t('mistakes.explanation_locked')}
          <Link to={`/portal/results/${attemptId}`} className="font-medium text-foreground underline underline-offset-2">
            {t('mistakes.open_result')}
          </Link>
        </p>
      )}
    </>
  );
}

/**
 * One mistake to put right: answer it again first; a right answer fixes it (and earns points if the answer
 * was not revealed). Revealing is the way out, and then it can be marked learned.
 */
export default function MistakeCard({ mistake }: { mistake: Mistake }) {
  const { t } = useTranslation();
  const retry = useRetryMistake();
  const reveal = useRevealMistake();
  const markLearned = useMarkLearned();
  const [answer, setAnswer] = useState<RetryAnswer>(() => (mistake.retry ? emptyAnswer(mistake.retry) : ''));
  const [wrongTries, setWrongTries] = useState(0);
  const result = retry.data?.correct ? retry.data : null;
  const busy = retry.isPending || reveal.isPending;

  const check = () => {
    if (!mistake.retry || !isAnswered(mistake.retry, answer)) return;
    retry.mutate({ mistakeKey: mistake.key, answer }, {
      onSuccess: (r) => { if (!r.correct) setWrongTries((n) => n + 1); },
    });
  };

  return (
    <li className={cn('rounded-lg border bg-card transition-colors', result ? 'border-success-300 dark:border-success-800' : 'border-border')}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2.5 text-[12.5px] text-muted-foreground">
        <span className="min-w-0 truncate">
          {mistake.testTitle}
          {mistake.passageTitle ? ` · ${mistake.passageTitle}` : ''}
        </span>
        {mistake.answeredAt && <time dateTime={mistake.answeredAt}>{new Date(mistake.answeredAt).toLocaleDateString()}</time>}
      </div>
      <div className="space-y-3 px-4 py-3.5">
        <RenderedHtml html={mistake.prompt} className="text-[14.5px] leading-6 text-foreground" />
        <p className="text-[13px] text-muted-foreground">
          {t('mistakes.your_answer')}:{' '}
          <span className="font-medium text-error-700 line-through decoration-error-400/70 dark:text-error-400">
            {mistake.yourAnswer ? <RenderedHtml html={mistake.yourAnswer} inline /> : '—'}
          </span>
        </p>

        {result ? (
          <>
            <p role="status" className="flex items-center gap-2 text-[14px] font-semibold text-success-700 dark:text-success-400">
              <CircleCheck className="h-4 w-4" aria-hidden />
              {result.pointsAwarded > 0 ? t('mistakes.fixed_points', { points: result.pointsAwarded }) : t('mistakes.fixed')}
            </p>
            <AnswerReveal answer={result} attemptId={mistake.attemptId} />
          </>
        ) : mistake.revealed ? (
          <AnswerReveal answer={mistake} attemptId={mistake.attemptId} />
        ) : mistake.retry ? (
          <form
            className="space-y-2.5"
            onSubmit={(e) => { e.preventDefault(); check(); }}
          >
            <RetryInput form={mistake.retry} value={answer} onChange={setAnswer} disabled={busy} />
            {wrongTries > 0 && (
              <p role="status" className="text-[13px] font-medium text-warning-700 dark:text-warning-400">
                {t('mistakes.still_wrong', { count: wrongTries })}
              </p>
            )}
            {(retry.isError || reveal.isError) && (
              <p role="alert" className="text-[13px] text-error-700 dark:text-error-400">{t('mistakes.action_failed')}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" size="sm" className="gap-1.5" disabled={busy || !isAnswered(mistake.retry, answer)}>
                <RotateCcw className="h-4 w-4" aria-hidden />
                {t('mistakes.check')}
              </Button>
              <Button type="button" variant="ghost" size="sm" className="gap-1.5" disabled={busy} onClick={() => reveal.mutate(mistake.key)}>
                <Eye className="h-4 w-4" aria-hidden />
                {t('mistakes.show_answer')}
              </Button>
            </div>
            <p className="text-[12px] text-muted-foreground">{t('mistakes.reveal_note')}</p>
          </form>
        ) : (
          <div className="space-y-2">
            <p className="text-[13px] text-muted-foreground">{t('mistakes.reveal_only')}</p>
            {reveal.isError && <p role="alert" className="text-[13px] text-error-700 dark:text-error-400">{t('mistakes.action_failed')}</p>}
            <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={() => reveal.mutate(mistake.key)}>
              <Eye className="h-4 w-4" aria-hidden />
              {t('mistakes.show_answer')}
            </Button>
          </div>
        )}
      </div>
      {mistake.revealed && !result && (
        <div className="flex justify-end border-t border-border px-4 py-2.5">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            disabled={markLearned.isPending}
            onClick={() => markLearned.mutate(mistake.key)}
          >
            <Check className="h-4 w-4" aria-hidden />
            {t('mistakes.mark_learned')}
          </Button>
        </div>
      )}
    </li>
  );
}
